import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { skyU } from './skyState';
import { QUALITY } from './quality';

// ── THE SKY, COMPUTED RATHER THAN PHOTOGRAPHED ───────────────────────────────
//
// The four Gemini panoramas were pretty stills with three problems a game cannot
// hide: their sun was never where the light came from, they crossfaded on their
// own schedule, and as equirectangulars they were not quite equirectangular (a
// seam down the day sky, a smeared edge at dawn, black fisheye corners at night).
//
// This shades the sky per pixel from the shared sky state: an atmosphere
// gradient, the sun's disc and halo exactly where the directional light is, a
// moving cloud deck lit by that same sun, stars and a moon at night, and the
// orange glow a city throws up under its own haze. Nothing is a texture, so
// nothing has a seam, and every hour is its own sky instead of a blend of four.
//
// DIRECTION-BASED. The colour depends only on which way the pixel looks, never
// on where the surface is — so the far dome and CityBox's nearer backdrop shell
// can wear the same material and be indistinguishable. That is what lets the
// shell keep doing its job (a horizon close enough for the skyline to parallax
// against) without a second sky to keep in step.

const VERT = /* glsl */`
varying vec3 vWorld;
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorld = wp.xyz;
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

const FRAG = /* glsl */`
uniform vec3 uSunDir;
uniform vec3 uMoonDir;
uniform vec3 uSunColor;
uniform vec3 uZenith;
uniform vec3 uHorizon;
uniform vec3 uGround;
uniform vec3 uCloudLit;
uniform vec3 uCloudDark;
uniform vec3 uCityGlow;
uniform float uNight;
uniform float uStars;
uniform float uSunVis;
uniform float uCloudCover;
uniform float uTime;
varying vec3 vWorld;

float hash12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float hash13(vec3 p3) { p3 = fract(p3 * 0.1031); p3 += dot(p3, p3.zyx + 31.32); return fract((p3.x + p3.y) * p3.z); }
float vnoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), u.x),
             mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  mat2 r = mat2(1.6, 1.2, -1.2, 1.6);
  for (int i = 0; i < CLOUD_OCTAVES; i++) { v += a * vnoise(p); p = r * p; a *= 0.5; }
  return v;
}

void main() {
  vec3 dir = normalize(vWorld - cameraPosition);
  float y = dir.y;
  float h = clamp(y, 0.0, 1.0);
  float mu = dot(dir, uSunDir);
  float muP = max(mu, 0.0);

  // Atmosphere: horizon → zenith, with the bright band right at the horizon
  // that every real sky has (you look through the most air there).
  vec3 col = mix(uHorizon, uZenith, pow(h, 0.45));
  col += uHorizon * 0.22 * exp(-h * 16.0);
  // The half of the sky the sun is in runs warmer, mostly low down.
  float sunSide = pow(mu * 0.5 + 0.5, 5.0);
  col += uSunColor * sunSide * 0.18 * exp(-h * 3.2) * uSunVis;
  // Mie halo around the sun.
  col += uSunColor * (pow(muP, 10.0) * 0.32 + pow(muP, 160.0) * 1.8) * uSunVis;
  // City light pollution: an orange skirt low on the horizon at night.
  col += uCityGlow * exp(-h * 9.0);

  // Stars — a hashed lattice on the direction sphere, twinkling, hidden by
  // the horizon haze and gone by day.
  #if STARS
  if (uStars > 0.01 && y > 0.0) {
    vec3 sp = dir * 380.0;
    vec3 ip = floor(sp);
    float r = hash13(ip);
    if (r > 0.9958) {
      vec3 fp = fract(sp) - 0.5;
      float s = smoothstep(0.42, 0.0, length(fp));
      float tw = 0.65 + 0.35 * sin(uTime * (1.5 + r * 5.0) + r * 91.0);
      col += vec3(0.9, 0.95, 1.0) * s * tw * uStars * smoothstep(0.02, 0.3, y) * 1.6;
    }
  }
  #endif

  // Below the horizon: the far ground, seen through haze.
  col = mix(col, uGround, smoothstep(0.0, -0.06, y));

  // Cloud deck — a plane overhead, projected per pixel, drifting on the wind.
  float cloud = 0.0;
  #if CLOUDS
  if (y > 0.0) {
    vec2 uv = dir.xz / (y + 0.09) * 0.85 + vec2(uTime * 0.0045, uTime * 0.0018);
    float warp = fbm(uv * 0.7 + 3.1);
    float n = fbm(uv * 1.5 + warp * 0.8);
    float lo = 1.0 - uCloudCover - 0.12;
    cloud = smoothstep(lo, lo + 0.32, n);
    // Self-shadow: a second sample stepped toward the sun. Cloud that has more
    // cloud between it and the sun is darker — the cheap version of scattering,
    // and the thing that turns noise into weather.
    float n2 = fbm(uv * 1.5 + warp * 0.8 + normalize(uSunDir.xz + 1e-4) * 0.09);
    float lit = clamp(0.55 + (n - n2) * 4.0, 0.0, 1.0);
    vec3 cc = mix(uCloudDark, uCloudLit, lit);
    // Silver lining: thin cloud near the sun glows.
    cc += uSunColor * pow(muP, 5.0) * (1.0 - cloud) * 0.9 * uSunVis;
    float fade = smoothstep(0.0, 0.12, y);
    cloud *= fade;
    col = mix(col, cc, cloud * 0.93);
  }
  #endif

  // The sun's disc (bloom makes it burn), dimmed by cloud in front of it.
  float disc = smoothstep(0.99962, 0.99982, mu);
  col += uSunColor * disc * 14.0 * uSunVis * (1.0 - cloud * 0.85);

  // The moon, and its small cold halo.
  float mm = dot(dir, uMoonDir);
  // Only once the sun has truly gone — a full moon hanging over golden hour
  // reads as a mistake, however possible it is in life.
  float moonVis = smoothstep(0.35, 0.9, uNight);
  col += vec3(0.88, 0.92, 1.0) * smoothstep(0.99975, 0.99985, mm) * 1.7 * moonVis * (1.0 - cloud * 0.7);
  col += vec3(0.25, 0.32, 0.55) * pow(max(mm, 0.0), 220.0) * 0.4 * moonVis;

  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** A sky material bound to the shared sky uniforms. Every instance updates together. */
export function createSkyMaterial() {
  const mat = new THREE.ShaderMaterial({
    uniforms: skyU,
    vertexShader: VERT,
    fragmentShader: FRAG,
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    defines: {
      CLOUD_OCTAVES: Math.max(1, QUALITY.cloudOctaves || 4),
      CLOUDS: QUALITY.cloudsOn ? 1 : 0,
      STARS: 1,
    },
  });
  mat.name = 'proceduralSky';
  return mat;
}

const R = 900;

export default function ProceduralSky() {
  const mat = useMemo(createSkyMaterial, []);
  useEffect(() => () => mat.dispose(), [mat]);
  return (
    <mesh renderOrder={-11} frustumCulled={false} material={mat}>
      <sphereGeometry args={[R, 48, 24]} />
    </mesh>
  );
}
